// @ts-check
// Formulários das esperas (spec 2.0.0, fluxos.md seção 20.7; contrato 09 de fluxos, seção 17): "Esperar" (dias, horas e
// minutos), "Esperar e agendar hora" (dias e a faixa de horário, com o exemplo calculado) e "Esperar e agendar data e
// hora" (uma data e hora fixa ou uma data do lead, antes ou depois). Tudo no horário de Brasília.
import { html } from 'htm/preact';
import { FLOW_TIME_ZONE, parseClockTime } from '../../../../../shared/flows/flow-wait-time.js';
import { localParts, localToInstant } from '../../../../../shared/time/zoned-time.js';
import { MAXIMUM_WAIT_DAYS, MAXIMUM_WAIT_OFFSET_AMOUNT } from '../../../../../shared/flows/flow-limits.js';
import { translateMetadataLabel, t } from '../../../i18n/index.js';
import { cardObject } from '../flow-lookups.js';
import { FlowField, FlowNotice, fieldProblemText, flowProblemText } from '../flow-ui.js';
import { FlowRadioOption } from '../flow-lifecycle-dialogs.js';

/** @typedef {import('../flow-node-panel.js').NodeFormProps} NodeFormProps */

// hora de chegada usada no exemplo da faixa de horário
const EXAMPLE_ARRIVAL_MINUTES = 15 * 60;

/** Número inteiro de um campo (vazio vira 0). @param {string} value */
const integerOf = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Pendência de espera do passo, como texto. @param {NodeFormProps['problems']} problems */
const waitProblemText = (problems) => {
  const problem = problems.find((candidate) => candidate.code === 'WAIT_INVALID' || candidate.code === 'DATE_IN_PAST');
  return problem ? flowProblemText(problem) : null;
};

/**
 * Exemplo da faixa de horário: "Quem chegar hoje às 15:00 segue amanhã entre 08:00 e 10:00."
 * @param {{ days: number, windowStart: string, windowEnd: string }} config
 * @returns {string | null}
 */
export function waitWindowExample(config) {
  const start = parseClockTime(config.windowStart);
  const end = parseClockTime(config.windowEnd);
  if (start === null || end === null || end <= start) return null;
  const days = Math.max(0, Math.trunc(Number(config.days) || 0));
  let offset = days;
  if (days === 0) {
    if (EXAMPLE_ARRIVAL_MINUTES >= start && EXAMPLE_ARRIVAL_MINUTES < end) return t('flows.forms.waitWindow.exampleNow');
    offset = EXAMPLE_ARRIVAL_MINUTES >= end ? 1 : 0;
  }
  const day = offset === 0 ? t('flows.forms.waitWindow.today') : offset === 1 ? t('flows.forms.waitWindow.tomorrow') : t('flows.forms.waitWindow.inDays', { count: offset });
  return t('flows.forms.waitWindow.example', { day, start: config.windowStart, end: config.windowEnd });
}

/** @param {NodeFormProps} props */
export function WaitForm({ node, problems, settings, onConfig }) {
  const config = node.config;
  const error = waitProblemText(problems);
  /** @param {'days' | 'hours' | 'minutes'} key @param {number} maximum */
  const numberField = (key, maximum) => html`<label class="flow-wait-unit">
    <input type="number" class="text-input" min="0" max=${maximum} step="1" value=${String(config[key] ?? 0)} inputMode="numeric"
      aria-invalid=${error ? 'true' : undefined}
      onInput=${(/** @type {InputEvent} */ event) => onConfig({ [key]: Math.max(0, integerOf(/** @type {HTMLInputElement} */ (event.currentTarget).value)) })} />
    <span>${t(`flows.forms.wait.${key}`)}</span>
  </label>`;
  return html`<div class="flow-node-form" data-testid="flow-wait-form">
    <${FlowField} label=${t('flows.forms.wait.label')} error=${error} hint=${t('flows.forms.wait.hint')}>
      <div class="flow-wait-row">${numberField('days', MAXIMUM_WAIT_DAYS)}${numberField('hours', 23)}${numberField('minutes', 59)}</div>
    <//>
    ${settings.considerWeekends === false ? html`<${FlowNotice} icon="IconCalendarX">${t('flows.forms.wait.weekendsOff')}<//>` : null}
  </div>`;
}

/** @param {NodeFormProps} props */
export function WaitWindowForm({ node, problems, settings, onConfig }) {
  const config = node.config;
  const error = waitProblemText(problems);
  const example = waitWindowExample(/** @type {any} */ (config));
  return html`<div class="flow-node-form" data-testid="flow-wait-window-form">
    <${FlowField} label=${t('flows.forms.waitWindow.wait')} htmlFor=${`flow-window-days-${node.id}`}>
      <div class="flow-wait-row">
        <label class="flow-wait-unit">
          <input id=${`flow-window-days-${node.id}`} type="number" class="text-input" min="0" max=${MAXIMUM_WAIT_DAYS} value=${String(config.days ?? 0)} inputMode="numeric"
            onInput=${(/** @type {InputEvent} */ event) => onConfig({ days: Math.max(0, integerOf(/** @type {HTMLInputElement} */ (event.currentTarget).value)) })} />
          <span>${t('flows.forms.wait.days')}</span>
        </label>
      </div>
    <//>
    <${FlowField} label=${t('flows.forms.waitWindow.between')} error=${error} hint=${t('flows.forms.waitWindow.timeZoneHint')}>
      <div class="flow-wait-row">
        <input type="time" class="text-input" step="900" value=${config.windowStart} aria-label=${t('flows.forms.waitWindow.start')}
          onInput=${(/** @type {InputEvent} */ event) => onConfig({ windowStart: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
        <span class="flow-wait-separator">${t('flows.forms.waitWindow.and')}</span>
        <input type="time" class="text-input" step="900" value=${config.windowEnd} aria-label=${t('flows.forms.waitWindow.end')}
          onInput=${(/** @type {InputEvent} */ event) => onConfig({ windowEnd: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
      </div>
    <//>
    ${example ? html`<${FlowNotice} icon="IconBulb" tone="tip">${example}<//>` : null}
    ${settings.considerWeekends === false ? html`<${FlowNotice} icon="IconCalendarX">${t('flows.forms.wait.weekendsOff')}<//>` : null}
  </div>`;
}

/** Data (AAAA-MM-DD) e hora (HH:MM) de Brasília de um instante. @param {string | null} at */
export function brasiliaInputValues(at) {
  const instant = at ? Date.parse(at) : Number.NaN;
  if (Number.isNaN(instant)) return { date: '', time: '' };
  const parts = localParts(instant, FLOW_TIME_ZONE);
  const pad = (/** @type {number} */ value) => String(value).padStart(2, '0');
  return { date: `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`, time: `${pad(parts.hour)}:${pad(parts.minute)}` };
}

/** Instante de uma data e hora de Brasília. @param {string} date @param {string} time @returns {string | null} */
export function brasiliaInstant(date, time) {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(time || '09:00');
  if (!dateMatch || !timeMatch) return null;
  const instant = localToInstant(Number(dateMatch[1]), Number(dateMatch[2]), Number(dateMatch[3]), Number(timeMatch[1]), Number(timeMatch[2]), FLOW_TIME_ZONE);
  return new Date(instant).toISOString();
}

/** Campos de data do card (DATE e DATE_TIME) para "uma data do lead". */
function cardDateFields() {
  return (cardObject()?.fields ?? []).filter((field) => (field.type === 'DATE' || field.type === 'DATE_TIME') && field.isActive !== false)
    .map((field) => ({ name: field.name, label: translateMetadataLabel(field.label) }))
    .sort((first, second) => first.label.localeCompare(second.label, 'pt-BR'));
}

/** @param {NodeFormProps} props */
export function WaitUntilDateForm({ node, problems, onConfig, onReplaceConfig }) {
  const config = node.config;
  const error = waitProblemText(problems);
  const isField = config.mode === 'FIELD';
  const fixed = brasiliaInputValues(config.at ?? null);
  const amount = Math.abs(Number(config.offsetAmount ?? 0));
  const direction = Number(config.offsetAmount ?? 0) > 0 ? 'AFTER' : 'BEFORE';
  const fields = cardDateFields();
  /** @param {number} nextAmount @param {string} nextDirection */
  const setOffset = (nextAmount, nextDirection) => onConfig({ offsetAmount: nextDirection === 'AFTER' ? nextAmount : -nextAmount });
  const name = `flow-until-mode-${node.id}`;
  return html`<div class="flow-node-form" data-testid="flow-wait-until-form">
    <div class="flow-radio-group" role="radiogroup" aria-label=${t('flows.forms.waitUntil.mode')}>
      <${FlowRadioOption} name=${name} value="FIXED" checked=${!isField} title=${t('flows.forms.waitUntil.fixed')}
        onSelect=${() => onReplaceConfig({ mode: 'FIXED', at: null })} />
      <${FlowRadioOption} name=${name} value="FIELD" checked=${isField} title=${t('flows.forms.waitUntil.field')}
        onSelect=${() => onReplaceConfig({ mode: 'FIELD', fieldName: null, offsetAmount: 0, offsetUnit: 'HOURS' })} />
    </div>
    ${isField
      ? html`<${FlowField} label=${t('flows.forms.waitUntil.fieldLabel')} htmlFor=${`flow-until-field-${node.id}`} error=${fieldProblemText(problems, 'fieldName')}>
          <select id=${`flow-until-field-${node.id}`} class="text-input" value=${config.fieldName ?? ''}
            onChange=${(/** @type {Event} */ event) => onConfig({ fieldName: /** @type {HTMLSelectElement} */ (event.currentTarget).value || null })}>
            <option value="">${t('flows.forms.chooseField')}</option>
            ${fields.map((field) => html`<option key=${field.name} value=${field.name}>${field.label}</option>`)}
          </select>
        <//>
        <${FlowField} label=${t('flows.forms.waitUntil.offset')} error=${error}>
          <div class="flow-wait-row">
            <input type="number" class="text-input flow-wait-amount" min="0" max=${MAXIMUM_WAIT_OFFSET_AMOUNT} value=${String(amount)} inputMode="numeric"
              aria-label=${t('flows.forms.waitUntil.amount')}
              onInput=${(/** @type {InputEvent} */ event) => setOffset(Math.max(0, integerOf(/** @type {HTMLInputElement} */ (event.currentTarget).value)), direction)} />
            <select class="text-input" value=${config.offsetUnit ?? 'HOURS'} aria-label=${t('flows.forms.waitUntil.unit')}
              onChange=${(/** @type {Event} */ event) => onConfig({ offsetUnit: /** @type {HTMLSelectElement} */ (event.currentTarget).value })}>
              ${['MINUTES', 'HOURS', 'DAYS'].map((unit) => html`<option key=${unit} value=${unit}>${t(`flows.forms.waitUntil.units.${unit}`)}</option>`)}
            </select>
            <select class="text-input" value=${direction} aria-label=${t('flows.forms.waitUntil.direction')}
              onChange=${(/** @type {Event} */ event) => setOffset(amount, /** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
              <option value="BEFORE">${t('flows.summary.direction.BEFORE')}</option>
              <option value="AFTER">${t('flows.summary.direction.AFTER')}</option>
            </select>
          </div>
        <//>`
      : html`<${FlowField} label=${t('flows.forms.waitUntil.fixedLabel')} error=${fieldProblemText(problems, 'at') ?? error} hint=${t('flows.forms.waitWindow.timeZoneHint')}>
          <div class="flow-wait-row">
            <input type="date" class="text-input" value=${fixed.date} aria-label=${t('flows.forms.waitUntil.date')}
              onInput=${(/** @type {InputEvent} */ event) => onConfig({ at: brasiliaInstant(/** @type {HTMLInputElement} */ (event.currentTarget).value, fixed.time || '09:00') })} />
            <input type="time" class="text-input" step="900" value=${fixed.time} aria-label=${t('flows.forms.waitUntil.time')} disabled=${!fixed.date}
              onInput=${(/** @type {InputEvent} */ event) => onConfig({ at: brasiliaInstant(fixed.date, /** @type {HTMLInputElement} */ (event.currentTarget).value) })} />
          </div>
        <//>`}
    <${FlowNotice} icon="IconHierarchy2">${t('flows.forms.waitUntil.branches')}<//>
    <p class="flow-field-hint">${t('flows.forms.waitUntil.noWeekends')}</p>
  </div>`;
}
